import { ReactNode, useContext } from "react";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TaskGroupBy } from "@common/types/task";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { formatDateOnly } from "@helpers/date";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { WorkspaceAvatarGroup } from "@components/avatar/WorkspaceAvatarGroup";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    TaskClientIconColorsContext,
    TaskClientNamesContext,
} from "../../taskClientNamesContext";
import { NestedTaskRow } from "../../types";
import { getGroupRowSummary, TaskGroupSummary } from "./taskGroupSummary";

type GroupCellProps<TValue> = CellContext<
    ViewTableFeatures,
    NestedTaskRow,
    TValue
>;

interface CollapsedGroupRowSummaryProps {
    row: GroupCellProps<unknown>["row"];
    hiddenWhenGroupedBy: TaskGroupBy | null;
    children: (summary: TaskGroupSummary | null) => ReactNode;
}

/** Renders `children` with the group row's summary, or `null` while the row is expanded. */
const CollapsedGroupRowSummary = ({
    row,
    hiddenWhenGroupedBy,
    children,
}: CollapsedGroupRowSummaryProps) => (
    <RowExpansion row={row}>
        {(isExpanded) =>
            children(
                isExpanded
                    ? null
                    : getGroupRowSummary(row.original, hiddenWhenGroupedBy),
            )
        }
    </RowExpansion>
);

export const GroupEstimateSummary = ({
    row,
}: GroupCellProps<number | null>) => (
    <CollapsedGroupRowSummary row={row} hiddenWhenGroupedBy={null}>
        {(summary) =>
            summary?.estimatedTime == null ? null : (
                <ViewTableCell>
                    {formatDuration(summary.estimatedTime)}
                </ViewTableCell>
            )
        }
    </CollapsedGroupRowSummary>
);

export const GroupDueDateSummary = ({ row }: GroupCellProps<Date | null>) => (
    <CollapsedGroupRowSummary
        row={row}
        hiddenWhenGroupedBy={TaskGroupBy.DUE_DATE}
    >
        {(summary) =>
            !summary?.dueDate ? null : (
                <ViewTableCell>
                    {formatDateOnly(summary.dueDate, fullDateFullYearFormat)}
                </ViewTableCell>
            )
        }
    </CollapsedGroupRowSummary>
);

export const GroupAssigneesSummary = ({ row }: GroupCellProps<unknown>) => (
    <CollapsedGroupRowSummary
        row={row}
        hiddenWhenGroupedBy={TaskGroupBy.ASSIGNEE}
    >
        {(summary) =>
            !summary?.assignees.length ? null : (
                <ViewTableCell>
                    <AvatarGroup
                        items={summary.assignees}
                        getKey={(assignee) => assignee.id}
                        getLabel={(assignee) => assignee.name}
                        renderAvatar={(assignee) => (
                            <AvatarTooltip content={assignee.name}>
                                <UserAvatar user={assignee} />
                            </AvatarTooltip>
                        )}
                        max={3}
                    />
                </ViewTableCell>
            )
        }
    </CollapsedGroupRowSummary>
);

export const GroupClientsSummary = ({ row }: GroupCellProps<string>) => {
    const clientNamesById = useContext(TaskClientNamesContext);
    const iconColorsById = useContext(TaskClientIconColorsContext);
    return (
        <CollapsedGroupRowSummary
            row={row}
            hiddenWhenGroupedBy={TaskGroupBy.CLIENT}
        >
            {(summary) => {
                const workspaces = (summary?.workspaceIds ?? []).flatMap(
                    (id) => {
                        const name = clientNamesById.get(id);
                        return name
                            ? [
                                  {
                                      workspace: {
                                          id,
                                          name,
                                          iconColor:
                                              iconColorsById.get(id) ?? null,
                                      },
                                  },
                              ]
                            : [];
                    },
                );
                if (workspaces.length === 0) {
                    return null;
                }
                return (
                    <ViewTableCell>
                        <WorkspaceAvatarGroup workspaces={workspaces} max={3} />
                    </ViewTableCell>
                );
            }}
        </CollapsedGroupRowSummary>
    );
};
